import { assign, cloneDeep } from 'lodash-es'
import { watch } from 'vue'
import type { LoginStatus, UserAuthState } from '@/store/modules/auth/type'
import { PermissionRole, UserAccountType, UserViewType } from '@/store/modules/auth/type'
import { callbackWithoutRedirect, getPermissionGroup, getRdxToken, loginTgc, refreshSession, logout as userLogout } from '@/api/auth'
import { clearSimulated } from '@/utils/simulated'
import { clearCsToken, clearCustomerId, clearToken, setToken } from '@/utils/auth'
import { removeSessionStorage, saveCookie } from '@/utils/cache'
import { COOKIE_JESSSION_ID_KEY } from '@/constant/cache-key'
import store from '@/store'
import { LOGIN_SUCCESS } from '@/constant/event-name-key'
import { setUserId, trackPageView } from '@/directives/track/utils'
import { clearLoginCallbackUrl, getLoginEntryUrl, isLocalDebugDomain, resolveAuthRedirectUrl } from '@/utils/auth-redirect'

const enableLocalDebugUserDefaults = import.meta.env.DEV
const SESSION_RESET_KEYS = ['globalSearch', 'stp_header_view_mode'] as const
// const enableLocalDebugUserDefaults = false

const defaultUser: UserAuthState = {
  name: '',
  avatar: '',
  email: '',
  current_enterprise: '',
  permissionGroup: [],
  user_name: '',
  nick_name: '',
  tenant_id: '',
  user_id: '',
  dept_id: [],
  role_id: [],
  role_name: [],
  enterprise_id: [],
  account: '',
  access_token: '', // 鉴权的token
  token_type: '',
  expires_in: 0,
  license: '',
  simulated_login_flag: 0,
  simulated_login_ticket: '',
  current_enterprise_name: '',
  current_enterprise_name_cn: '',
  is_admin: false,
  highest_authority_organization_id: '',
  role_permission: PermissionRole.Guest,
  user_account_types: [],
  // 生产默认态保持为空，避免未完成登录回填前就被写死成某个 BU / 视图权限。
  // 仅本地调试环境保留最小兜底，方便联调 Header 和 BU 首页。
  bu_ids: enableLocalDebugUserDefaults ? ['TIM BU'] : [],
  user_view_types: enableLocalDebugUserDefaults ? [UserViewType.Broker, UserViewType.Bu] : [],
}

const useAuthStore = defineStore('auth', () => {
  const user = ref<UserAuthState>(cloneDeep(defaultUser))
  const loginStatus = ref<LoginStatus>(0)
  // access token 到期时间只保留在内存中，浏览器持久化状态中不得出现 refresh_token 或 expiresAt。
  const expiresAt = ref<number | null>(null)
  const lastSessionRenewalFailureReason = ref<string | null>(null)
  let sessionRenewalPromise: Promise<boolean> | null = null
  const SESSION_RENEWAL_SAFETY_WINDOW_MS = 60 * 1000
  // 是否是超级管理员
  const superAdmin = computed(() => user.value.user_id === 'SssUser0001')

  // 账号类型缓存：
  // user_account_types 表示“账号身份”，更多用于历史权限判断和业务模块开关，
  // 例如 Broker / Customer / SuperAdmin / BU 等。
  // 它不直接决定首页 Header 上是否允许切换视图。
  const permissionCache = computed(() => {
    const types = user.value.user_account_types
    return {
      types,
      length: types.length,
      hasSuper: types.includes(UserAccountType.SuperAdmin),
      hasBroker: types.includes(UserAccountType.Broker),
      hasCustomer: types.includes(UserAccountType.Customer),
      hasCustomerService: types.includes(UserAccountType.CustomerService),
      hasBu: types.includes(UserAccountType.Bu),
    }
  })
  // 视图类型缓存：
  // user_view_types 表示“当前用户允许查看的数据视角”，
  // 是新版 BU / Broker 首页切换的直接依据。
  // 例如：
  // - ['BROKER_VIEW'] => 只能看 Broker 视角
  // - ['BU_VIEW', 'BROKER_VIEW'] => Header 上允许切换视角
  const viewTypeCache = computed(() => {
    const sourceTypes = user.value.user_view_types?.length ? user.value.user_view_types : [UserViewType.Broker]
    const types = [UserViewType.Bu, UserViewType.Broker].filter((type: UserViewType) => sourceTypes.includes(type))
    return {
      types,
      length: types.length,
      hasBroker: types.includes(UserViewType.Broker),
      hasBu: types.includes(UserViewType.Bu),
    }
  })

  // 包含权限检查
  const includeSuperView = computed(() => permissionCache.value.hasSuper)
  const includeBrokerView = computed(() => permissionCache.value.hasBroker)
  const includeCustomerView = computed(() => permissionCache.value.hasCustomer)
  const includeCustomerServiceView = computed(() => permissionCache.value.hasCustomerService)
  const includeBuAccountType = computed(() => permissionCache.value.hasBu)
  const hasBrokerUserViewType = computed(() => viewTypeCache.value.hasBroker)
  const hasBuUserViewType = computed(() => viewTypeCache.value.hasBu)

  // 单一权限检查 - 优化版本
  const onlySuperView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.SuperAdmin
  })
  const onlyBrokerView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.Broker
  })
  const onlyCustomerView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.Customer
  })
  const onlyCustomerServiceView = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.CustomerService
  })

  const onlyBuAccountType = computed(() => {
    const cache = permissionCache.value
    return cache.length === 1 && cache.types[0] === UserAccountType.Bu
  })

  // 隐藏佣金模块 - 优化版本
  const hiddenCommissionModule = computed(() => {
    const cache = permissionCache.value

    // 只有客户权限
    if (cache.length === 1 && cache.hasCustomer) {
      return true
    }

    // 客户和客服权限的组合
    if (cache.length === 2 && cache.hasCustomer && cache.hasCustomerService) {
      return true
    }

    return false
  })

  function setInfo(newUser: Partial<UserAuthState>) {
    // 用户信息会分多次回填：
    // 1. getRdxToken 返回基础用户信息
    // 2. getPermissionGroup 返回权限组
    // 因此这里使用 merge，而不是整对象替换。
    const { refresh_token: _refreshToken, ...safeUser } = newUser as Partial<UserAuthState> & { refresh_token?: unknown }
    user.value = assign({}, user.value, safeUser)
    // 兼容已持久化的旧 user.refresh_token，并确保之后的持久化快照不再携带它。
    delete (user.value as UserAuthState & { refresh_token?: unknown }).refresh_token
  }

  function resetInfo() {
    user.value = cloneDeep(defaultUser)
    expiresAt.value = null
  }

  function setLoginStatus(status: LoginStatus) {
    loginStatus.value = status
  }

  function applyAuthPayload(payload: Partial<UserAuthState> & { expires_in?: number }) {
    const { refresh_token: _refreshToken, expires_in: expiresIn, ...safePayload } = payload as Partial<UserAuthState> & { refresh_token?: unknown }
    setInfo(safePayload)
    setToken(safePayload.access_token ?? '')
    expiresAt.value = typeof expiresIn === 'number' && expiresIn > 0
      ? Date.now() + expiresIn * 1000
      : null
    lastSessionRenewalFailureReason.value = null
  }

  function shouldRenewSession() {
    return expiresAt.value !== null && Date.now() >= expiresAt.value - SESSION_RENEWAL_SAFETY_WINDOW_MS
  }

  function isTerminalSessionFailureReason(reason?: string | null) {
    return reason === 'permission_revoked' || reason === 'account_disabled'
  }

  function getAuthFailureReason(error: unknown) {
    const payload = error as { data?: { reason?: unknown, data?: { reason?: unknown } } }
    const reason = payload?.data?.reason ?? payload?.data?.data?.reason
    return typeof reason === 'string' ? reason : null
  }

  function extractAuthPayload(response: unknown) {
    const payload = response as Partial<UserAuthState> & { data?: Partial<UserAuthState> }
    // 新 refresh endpoint 返回平铺 OAuth 字段；保留对既有 { data } 响应的兼容。
    if (payload?.access_token || payload?.user_id) {
      return payload
    }
    return payload?.data ?? {}
  }

  async function fetchPermissionGroup(userId: string) {
    const permissionResponse = await getPermissionGroup(userId)
    const permissionGroup = permissionResponse.data ?? permissionResponse
    return Array.isArray(permissionGroup) ? permissionGroup : []
  }

  async function loadUser(authPayload?: Partial<UserAuthState>) {
    const { emit } = store.evenStore
    setLoginStatus(1)

    try {
      const response = authPayload ?? await getRdxToken<UserAuthState>()
      const data = extractAuthPayload(response)
      if (!data?.user_id) {
        resetInfo()
        clearToken()
        setLoginStatus(3)
        return
      }

      const nextUser: Partial<UserAuthState> = {
        ...data,
        permissionGroup: [],
      }

      try {
        nextUser.permissionGroup = await fetchPermissionGroup(data.user_id)
      }
      catch (error) {
        // permissionGroup 主要影响菜单/按钮级权限判断，不代表登录态本身失效。
        // 这里保留基础用户信息和 token，避免把临时接口异常放大成“被登出”。
        console.error('loadUser permissionGroup error', error)
      }

      // 登录链路至少需要“基础用户信息 + token”成功后才能进入已登录态。
      // permissionGroup 若临时失败，则以空数组兜底，避免网络抖动把用户误判成登录失效。
      applyAuthPayload(nextUser)
      const info = data.email ?? `${data.nick_name}(${data.user_id})`
      setUserId(info)
      trackPageView()
      setLoginStatus(2)
      emit(LOGIN_SUCCESS, { isCheck: true })
    }
    catch (error) {
      console.error('loadUser error', error)
      resetInfo()
      clearToken()
      setLoginStatus(3)
      throw error
    }
  }

  async function ensureSession(reason: 'request' | 'visibility' | 'unauthorized' = 'request') {
    // 终态失败必须保持到下一次成功认证，不能被一次新的 refresh 尝试清空。
    if (isTerminalSessionFailureReason(lastSessionRenewalFailureReason.value)) {
      return false
    }

    if (reason !== 'unauthorized' && !shouldRenewSession()) {
      return true
    }

    if (sessionRenewalPromise) {
      return sessionRenewalPromise
    }

    sessionRenewalPromise = (async () => {
      try {
        const response = await refreshSession<UserAuthState>()
        const data = extractAuthPayload(response)
        if (!data?.user_id || !data.access_token) {
          return false
        }
        // refresh 仅原子更新当前会话；不得复用 loadUser，以免触发登录中状态、埋点或登录事件。
        // 先安装新 access token，权限查询异常不能回滚已成功的 refresh。
        applyAuthPayload(data)
        if (!Array.isArray(data.permissionGroup)) {
          try {
            const permissionGroup = await fetchPermissionGroup(data.user_id)
            setInfo({ permissionGroup })
          }
          catch (error) {
            // 权限组主要用于前端展示和菜单控制；刷新成功后保留当前权限快照，避免因短暂异常丢弃新 token。
            console.error('ensureSession permissionGroup error', error)
          }
        }
        return true
      }
      catch (error) {
        lastSessionRenewalFailureReason.value = getAuthFailureReason(error)
        console.error(`ensureSession ${reason} error`, error)
        return false
      }
    })().finally(() => {
      sessionRenewalPromise = null
    })

    return sessionRenewalPromise
  }

  async function renewalRoles() {
    try {
      const { content } = await loginTgc<{ ticket?: string }>()
      if (typeof content !== 'object' || typeof content?.ticket !== 'string' || !content.ticket.trim()) {
        throw new Error('CAS renewal missing ticket')
      }

      const callbackResponse = await callbackWithoutRedirect<string>(content.ticket.trim())

      if (callbackResponse?.content) {
        saveCookie(COOKIE_JESSSION_ID_KEY, callbackResponse.content, { path: '/' })
      }

      await loadUser()
    }
    catch (error) {
      console.error('renewalRoles error', error)
      throw error
    }
  }

  function logoutCallBack() {
    // const store = useAppStore();
    resetInfo()
    setLoginStatus(0)
    clearToken()
    clearCsToken()
    clearCustomerId()
    clearSimulated()
    // 同一个浏览器 tab 重新登录时，sessionStorage 不会自动清空。
    // 这里显式移除全局筛选与历史 Header 视图缓存，避免新登录账号继承上一个会话的视图和筛选上下文。
    SESSION_RESET_KEYS.forEach((key) => {
      removeSessionStorage(key)
    })
    // removeRouteListener();
    // removeModalListener();
    // store.clearServerMenu();
  }

  // pinia-plugin-persistedstate 回灌旧版本快照后也会触发；立即删除历史 refresh_token。
  watch(user, (value) => {
    if ('refresh_token' in (value as object)) {
      delete (value as UserAuthState & { refresh_token?: unknown }).refresh_token
    }
  }, { deep: true, immediate: true })

  async function logout() {
    try {
      const data = await userLogout()
      logoutCallBack()
      const authRedirectUrl = resolveAuthRedirectUrl(data?.content)
      if (authRedirectUrl) {
        clearLoginCallbackUrl()
        window.location.replace(authRedirectUrl)
        return
      }
    }
    catch (error) {
      console.error('logout error', error)
      logoutCallBack()
    }

    clearLoginCallbackUrl()
    if (!isLocalDebugDomain()) {
      window.location.replace(getLoginEntryUrl())
    }
  }

  return {
    user,
    loginStatus,
    expiresAt,
    lastSessionRenewalFailureReason,
    superAdmin,
    includeSuperView,
    includeBrokerView,
    includeCustomerServiceView,
    includeCustomerView,
    includeBuAccountType,
    viewTypeCache,
    hasBrokerUserViewType,
    hasBuUserViewType,
    hiddenCommissionModule,
    onlySuperView,
    onlyBrokerView,
    onlyCustomerView,
    onlyCustomerServiceView,
    onlyBuAccountType,
    setInfo,
    resetInfo,
    applyAuthPayload,
    loadUser,
    ensureSession,
    renewalRoles,
    logoutCallBack,
    logout,
    setLoginStatus,
  }
}, {
  // https://prazdevs.github.io/pinia-plugin-persistedstate/zh/guide/config.html
  persist: {
    key: 'auth',
    paths: ['user'],
  },
})

export default useAuthStore
